<template>
  <MainLayout>
    <div class="container legal-page">
      <div class="legal-page__hero">
        <div>
          <h2 class="title text-uppercase mb-12 text-left">{{ pageTitle }}</h2>
        </div>
      </div>

      <div v-if="isLoading" class="legal-page__status">
        {{ loadingLabel }}
      </div>

      <div v-else-if="hasError" class="legal-page__status legal-page__status--error">
        {{ errorLabel }}
      </div>

      <div v-else class="legal-page__document">
        <div class="legal-page__last-updated">
          {{ lastUpdatedLabel }}
        </div>

        <div
          v-for="docSection in visibleSections"
          :id="docSection.id"
          :key="docSection.id"
          class="legal-page__section"
        >
          <h3 class="legal-page__section-title" v-html="localizedDoc(docSection.title)" />
          <div
            class="legal-page__section-content"
            v-html="localizedDoc(docSection.content)"
          />
        </div>
      </div>
    </div>
  </MainLayout>
</template>

<script>
import MainLayout from "@/components/layout/main-layout.vue";
import { legalContent } from "@/content/legal-content";
import {
  getCurrentMarket,
  normalizeLocale,
} from "@/utils/market-utils";
import { mapState } from "vuex";

const contentPaths = {
  privacy: "/content/privacy-policy.json",
  terms: "/content/terms-of-use.json",
};

export default {
  components: {
    MainLayout,
  },
  props: {
    pageKey: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      documentData: null,
      isLoading: true,
      hasError: false,
    };
  },
  computed: {
    ...mapState("configuration", ["actionBar"]),
    market() {
      return getCurrentMarket(this.actionBar && this.actionBar.country);
    },
    localeKey() {
      return normalizeLocale(this.$i18n.locale);
    },
    pageContent() {
      return legalContent[this.pageKey];
    },
    pageTitle() {
      return this.localized(this.pageContent.title);
    },
    intro() {
      return this.localized(this.pageContent.intro);
    },
    loadingLabel() {
      return this.localeKey === "fr" ? "Chargement..." : "Loading...";
    },
    errorLabel() {
      return this.localeKey === "fr"
        ? "Le document n'a pas pu être chargé."
        : "The document could not be loaded.";
    },
    lastUpdatedLabel() {
      if (!this.documentData || !this.documentData.lastUpdated) {
        return "";
      }
      const date = this.localizedDoc(this.documentData.lastUpdated);
      return this.localeKey === "fr"
        ? `Date de mise à jour : ${date}`
        : `Last updated: ${date}`;
    },
    visibleSections() {
      if (!this.documentData || !this.documentData.sections) {
        return [];
      }
      return this.documentData.sections.filter(
        (s) => s.market === null || s.market === this.market
      );
    },
  },
  mounted() {
    this.fetchContent();
  },
  methods: {
    localized(content) {
      return content[this.localeKey];
    },
    localizedDoc(content) {
      if (!content) return "";
      return content[this.localeKey] || content.fr || "";
    },
    async fetchContent() {
      this.isLoading = true;
      this.hasError = false;

      try {
        const path = contentPaths[this.pageKey];
        if (!path) throw new Error("Unknown page key");
        const response = await fetch(path);
        this.documentData = await response.json();
      } catch (error) {
        this.hasError = true;
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.legal-page {
  padding-top: 56px;
  padding-bottom: 72px;
}

.legal-page__hero {
  margin-bottom: 32px;
}

.legal-page__intro {
  max-width: 720px;
  line-height: 1.7;
}

.legal-page__status {
  padding: 18px 0;
}

.legal-page__status--error {
  color: $color-primary;
}

.legal-page__document {
  text-align: left;
}

.legal-page__last-updated {
  font-size: 14px;
  color: #888;
  margin-bottom: 28px;
}

.legal-page__section {
  margin-bottom: 32px;
  scroll-margin-top: 120px;

  &:last-child {
    margin-bottom: 0;
  }
}

.legal-page__section-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 16px;
  color: #272421;
  text-align: left;
}

.legal-page__section-content {
  line-height: 1.8;
  color: #333;
  text-align: left;

  ::v-deep {
    h4 {
      font-size: 16px;
      font-weight: 600;
      margin: 20px 0 10px;
      text-align: left;
    }

    p {
      margin: 0 0 12px;
      text-align: left;
    }

    ul {
      margin: 8px 0 16px 20px;
      padding: 0;
      list-style-type: disc;
    }

    li {
      margin-bottom: 6px;
      cursor: default;
    }

    a {
      color: $color-primary;
      text-decoration: underline;
    }

    strong {
      font-weight: 600;
    }
  }
}

@media screen and (max-width: $media-md) {
  .legal-page {
    padding-top: 36px;
    padding-bottom: 48px;
  }

  .legal-page__hero {
    flex-direction: column;
  }

  .legal-page__document {
    padding: 0;
  }
}
</style>
